import type { IntlShape } from 'react-intl';
import getMapboxAccessToken from '@alltrails/core/utils/getMapboxAccessToken';
import { AdminStyleSettings, BaseStyleId, StaticStyleId } from '../types/Styles';
import createRasterStyle from './createRasterStyle';
import type { MapboxStyleIds } from './getMapboxStyleIds';

export const getBaseStyle = ({
  adminStyleSettings,
  styleId,
  hideCustomAttribution,
  mapboxStyleIds,
  intl
}: {
  adminStyleSettings?: AdminStyleSettings;
  styleId: BaseStyleId;
  hideCustomAttribution: boolean;
  mapboxStyleIds: Pick<
    MapboxStyleIds,
    'mapboxAllTrailsStyleId' | 'mapboxAllTrailsStyleIdV2' | 'mapboxSatelliteStyleId' | 'mapbox3DBaseMapStyleId' | 'mapboxTerrainStyleId'
  >;
  intl: IntlShape;
}) => {
  const mapboxAccessToken = getMapboxAccessToken();

  switch (styleId) {
    case 'adminBasemap':
      return createRasterStyle(styleId, adminStyleSettings?.styleSourceUrls?.adminBasemap || '', {
        tileSize: 256,
        maxzoom: 19,
        attribution: hideCustomAttribution ? '' : `Custom Basemap ${adminStyleSettings?.styleSourceUrls?.adminBasemap}`
      });
    case 'alltrailsOutdoorsV2':
      return `https://api.mapbox.com/styles/v1/alltrails/${mapboxStyleIds.mapboxAllTrailsStyleId}?access_token=${mapboxAccessToken}`;
    case 'alltrailsOutdoorsV3':
      return `https://api.mapbox.com/styles/v1/alltrails/${mapboxStyleIds.mapboxAllTrailsStyleIdV2}?access_token=${mapboxAccessToken}`;
    case 'alltrailsSatellite':
      return `https://api.mapbox.com/styles/v1/alltrails/${mapboxStyleIds.mapboxSatelliteStyleId}?access_token=${mapboxAccessToken}`;
    case 'alltrailsSatelliteMinimal':
      return `https://api.mapbox.com/styles/v1/alltrails/${mapboxStyleIds.mapbox3DBaseMapStyleId}?access_token=${mapboxAccessToken}`;
    case 'googleSatellite':
      return createRasterStyle(styleId, 'https://mt1.google.com/vt/lyrs=s&x={x}&y={y}&z={z}', {
        attribution: 'Map data ©2023',
        tileSize: 256,
        maxzoom: 19
      });
    case 'ignPlan':
      return createRasterStyle(styleId, 'https://ign-france-plan.alltrails.com/{z}/{x}/{y}.png', {
        attribution: `© IGN - 2025 - ${intl.formatMessage({ defaultMessage: 'Copying and reproduction prohibited' })}`,
        tileSize: 256,
        maxzoom: 19
      });
    case 'ignScan':
      return createRasterStyle(styleId, 'https://ign-france-scan25.alltrails.com/{z}/{x}/{y}.jpeg', {
        attribution: `© IGN - 2025 - ${intl.formatMessage({ defaultMessage: 'Copying and reproduction prohibited' })}`,
        tileSize: 256,
        maxzoom: 16
      });
    case 'opencycle':
      return createRasterStyle(styleId, 'https://tiles-ocm.alltrails.com/{z}/{x}/{y}.png', {
        attribution: '© <a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
        tileSize: 256,
        maxzoom: 19
      });
    case 'openstreet':
      return createRasterStyle(styleId, 'https://tiles-osm.alltrails.com/{z}/{x}/{y}.png', {
        attribution: '© <a href="http://www.openstreetmap.org/copyright" target="_blank">OpenStreetMap</a> contributors',
        tileSize: 256,
        maxzoom: 19
      });
    case 'roadmap':
      return `https://api.mapbox.com/styles/v1/mapbox/streets-v10?access_token=${mapboxAccessToken}`;
    case 'topo':
      return createRasterStyle(styleId, 'https://us-topo.alltrails.com/{z}/{x}/{y}.png', {
        attribution: '© 2020 AllTrails',
        tileSize: 256,
        maxzoom: 15
      });
    case 'worldparks':
      return `https://api.mapbox.com/styles/v1/alltrails/${mapboxStyleIds.mapboxTerrainStyleId}?access_token=${mapboxAccessToken}`;
    case 'worldtopo':
      return createRasterStyle(styleId, 'https://tiles-wtopo.alltrails.com/{z}/{y}/{x}.png', {
        attribution: 'Copyright © 2013 ESRI',
        tileSize: 256,
        maxzoom: 19
      });
  }
};

export const getStaticStyle = ({
  adminStyleSettings,
  styleId,
  hideCustomAttribution
}: {
  adminStyleSettings?: AdminStyleSettings;
  styleId: StaticStyleId;
  hideCustomAttribution: boolean;
}) => {
  switch (styleId) {
    case 'adminOverlay':
      return createRasterStyle(
        styleId,
        adminStyleSettings?.styleSourceUrls?.adminOverlay || '',
        {
          tileSize: 256,
          maxzoom: 19,
          attribution: hideCustomAttribution ? '' : `Custom Overlay ${adminStyleSettings?.styleSourceUrls?.adminOverlay}`
        },
        { paint: { 'raster-opacity': 0.5 } }
      );
    case 'waymarkedCycling':
      return createRasterStyle(
        styleId,
        'https://waymarked-cycling.alltrails.com/{z}/{x}/{y}',
        {
          attribution: "<a target='_blank' href='https://http://www.waymarkedtrails.org/'>Waymarked Trails</a>",
          tileSize: 256,
          maxzoom: 16
        },
        { paint: { 'raster-opacity': 0.8 } }
      );
    case 'waymarkedHiking':
      return createRasterStyle(
        styleId,
        'https://waymarked-hiking.alltrails.com/{z}/{x}/{y}',
        {
          attribution: "<a target='_blank' href='https://www.waymarkedtrails.org/'>Waymarked Trails</a>",
          tileSize: 256,
          maxzoom: 16
        },
        { paint: { 'raster-opacity': 0.8 } }
      );
    case 'waymarkedMTB':
      return createRasterStyle(
        styleId,
        'https://waymarked-mtb.alltrails.com/{z}/{x}/{y}',
        {
          attribution: "<a target='_blank' href='https://www.waymarkedtrails.org/'>Waymarked Trails</a>",
          tileSize: 256,
          maxzoom: 16
        },
        { paint: { 'raster-opacity': 0.8 } }
      );
  }
};
